import { create } from "zustand";
import { errorTitle, friendlyMessage } from "@/api/errors";
import { ApiError } from "@/api/client";

export type ToastTone = "info" | "success" | "warning" | "error";

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
  detail?: string;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, "id">, ttlMs?: number) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],
  push: (t, ttlMs = t.tone === "error" ? 9000 : 4500) => {
    const id = nextId++;
    set({ toasts: [...get().toasts.slice(-4), { ...t, id }] });
    setTimeout(() => get().dismiss(id), ttlMs);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((x) => x.id !== id) }),
}));

export const toast = {
  success: (title: string, message?: string) => useToasts.getState().push({ tone: "success", title, message }),
  info: (title: string, message?: string) => useToasts.getState().push({ tone: "info", title, message }),
  warning: (title: string, message?: string) => useToasts.getState().push({ tone: "warning", title, message }),
  error: (err: unknown, title?: string) => {
    const detail = err instanceof ApiError ? [err.errorCode, err.requestId && `request ${err.requestId}`].filter(Boolean).join(" · ") : undefined;
    useToasts.getState().push({ tone: "error", title: title ?? errorTitle(err), message: friendlyMessage(err), detail });
  },
};
